"use client";
import { useMemo, useContext } from "react";
import isEmpty from "lodash/isEmpty";

import { getCountryNameUsingIso } from "@stadium/helpers/country/utils";

import { getCountryFlagUrl } from "@stadium/ui/src/shared/country-flag";

import { GLOBAL_COUNTRY_ISO } from "@stadium/helpers/country/constants";

import {
  ResponsiveDrawerDropdown,
  DropdownToggle,
  DropdownItem,
} from "@stadium/ui/src/components/bootstrap-dropdown";
import TippyTooltip from "@stadium/ui/src/components/tooltip/tooltip";
import Pill from "@stadium/ui/src/components/pill";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import styles from "./purge-main.module.scss";

export const COUNTRY_LIMIT_COUNT_4 = 4;
export const COUNTRY_LIMIT_COUNT_2 = 2;
export const COUNTRY_LIMIT_COUNT_1 = 1;

const ProductCountryListing = ({
  brands,
  countries,
  availableGlobally = false,
  className = "",
  limit = COUNTRY_LIMIT_COUNT_2,
  showIcon = true,
  sorting = true,
  hideCountryFlag = false,
}) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const brandsCountryList = useMemo(() => {
    if (countries || availableGlobally) {
      if (availableGlobally) {
        return [{ iso: GLOBAL_COUNTRY_ISO, name: getCountryNameUsingIso(GLOBAL_COUNTRY_ISO) }];
      }
      if (isEmpty(countries)) return [];
      const seen = new Set();
      const list = countries.reduce((acc, { iso, name }) => {
        const upper = iso?.toUpperCase();
        if (!upper || seen.has(upper)) return acc;
        seen.add(upper);
        acc.push({ iso: upper, name: name || getCountryNameUsingIso(iso) });
        return acc;
      }, []);
      return sorting ? list.sort((a, b) => a?.name?.localeCompare(b?.name)) : list;
    }

    if (isEmpty(brands)) return [];

    const anyGlobalZone = brands.some(({ global }) => global);

    if (anyGlobalZone) {
      return [{ iso: GLOBAL_COUNTRY_ISO, name: getCountryNameUsingIso(GLOBAL_COUNTRY_ISO) }];
    }

    const seen = new Set();
    const brandsCountryList = brands.reduce((acc, { countryCodes }) => {
      if (!countryCodes) return acc;
      countryCodes.forEach((code) => {
        const upper = code?.toUpperCase();
        if (!upper || seen.has(upper)) return;
        seen.add(upper);
        acc.push({ iso: upper, name: getCountryNameUsingIso(code) });
      });
      return acc;
    }, []);

    return sorting
      ? brandsCountryList.sort((a, b) => a?.name?.localeCompare(b?.name))
      : brandsCountryList;
  }, [brands, countries, availableGlobally, sorting]);

  const brandsCountryListExceedingThreshold = brandsCountryList.length - limit;

  const renderCountryDropDown = () => (
    <ResponsiveDrawerDropdown
      menuClassName={`${styles.flagDropdown} flagdropdown-for-mobile`}
      heading={
        isMobileDevice ? (
          <DropdownToggle showCloseIcon={true}>
            <span className="">Available in</span>
          </DropdownToggle>
        ) : (
          ""
        )
      }
      label={
        <DropdownToggle
          showDropdownIcon={showIcon}
          className={styles.ddToggleBtn}
          dropDownIconClass={styles.ddIcon}
        >
          <span className={styles.ddText}>
            +{brandsCountryListExceedingThreshold}
          </span>
        </DropdownToggle>
      }
    >
      {brandsCountryList
        .slice(limit, brandsCountryList.length)
        .map(({ iso, name }) => renderProductCountryFlag({ iso, name, isNameRequired: true }))}
    </ResponsiveDrawerDropdown>
  );

  const renderProductCountryFlag = ({ iso, name, isNameRequired = false }) => {
    const flagUrl = getCountryFlagUrl({ iso }, "round");

    return (
      <DropdownItem key={iso} className={styles.flagImageBox}>
        {hideCountryFlag && !isNameRequired && <Pill color="ice">{iso}</Pill>}
        {!hideCountryFlag && (
          <Multimedia
            type="image"
            src={flagUrl}
            width={24}
            height={24}
            isNextImage={false}
            className={`${styles.flagImage} img-flag`}
            alt="country-flag"
          />
        )}
        {isNameRequired && <span>{name}</span>}
      </DropdownItem>
    );
  };

  return (
    <div
      className={`${styles.productCountry} ${className} ${
        hideCountryFlag ? "" : styles.gapWithCountryFlags
      }`}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {brandsCountryList.slice(0, limit).map(({ name, iso }) => (
        <TippyTooltip
          heading={name}
          isLightTheme
          arrow
          interactive
          placement="top-end"
          key={iso}
          containerClass={styles.cmnSmallTip}
        >
          <span>{renderProductCountryFlag({ name, iso })}</span>
        </TippyTooltip>
      ))}
      {brandsCountryListExceedingThreshold > 0 && renderCountryDropDown()}
    </div>
  );
};

export default ProductCountryListing;
